<template>
  <v-menu v-model="menuOpen" :close-on-content-click="false" transition="scale-transition" location="bottom start">
    <template v-slot:activator="{ props: menuProps }">
      <v-text-field
        v-bind="{ ...menuProps, ...$attrs }"
        :model-value="displayValue"
        readonly
        style="cursor: pointer"
        @click:clear.stop="emitValue(null, null)"
        @keydown.esc="menuOpen = false"
      />
    </template>

    <v-card rounded="lg" elevation="8">
      <!-- Explicit accept/close affordances + manual Esc handling: the value
           is already committed as the user picks (no separate pending
           state), so both buttons just close the menu - the checkmark gives
           a clear "I'm done" action (most useful with withTime, which
           doesn't auto-close on date pick since time is still pending),
           while X stays for a plain dismiss. Vuetify's own Escape-to-close
           is wired through a module-global "is this overlay on top of every
           overlay in the whole app" check, which other transient overlays
           elsewhere (tooltips, menus) can leave stale, silently breaking
           Escape for this one with no visible cause. Click-outside uses a
           different, reliably-scoped check and keeps working either way,
           but visible buttons plus a direct Esc handler on the field mean
           closing never depends on that global state. -->
      <div class="d-flex justify-end ga-1 pt-1 pr-1">
        <v-btn icon="mdi-check" size="x-small" variant="tonal" color="primary" density="comfortable" @click="menuOpen = false" />
        <v-btn icon="mdi-close" size="x-small" variant="text" density="comfortable" @click="menuOpen = false" />
      </div>
      <v-date-picker :model-value="pickerValue" hide-header @update:model-value="onPickDate" />
      <div v-if="withTime" class="px-4 pb-4">
        <v-text-field
          :model-value="timeStr"
          type="time"
          :label="timeLabel"
          density="compact"
          hide-details
          @update:model-value="onTimeChange"
        />
      </div>
    </v-card>
  </v-menu>
</template>

<script setup>
// Drop-in replacement for `<v-text-field type="date">` /
// `<v-text-field type="datetime-local">` - a calendar popup (Vuetify's own
// v-date-picker) instead of the browser's native date widget, whose look
// varies a lot across browsers/OSes and is easy to fat-finger. Modeled on
// good-talk-web's AppDatePicker.vue.
//
// modelValue is a plain "YYYY-MM-DD" string (withTime=false, matches
// type="date") or "YYYY-MM-DDTHH:mm" (withTime=true, matches
// type="datetime-local") - the same shape the native inputs already
// produced, so callers don't need to change how they read/send the value.
// Every other v-text-field prop (label, hint, density, rules, clearable,
// ...) passes straight through via $attrs onto the visible field - only
// modelValue/withTime/timeLabel are intercepted here.
import { ref, computed } from 'vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: { type: String, default: null },
  withTime: { type: Boolean, default: false },
  timeLabel: { type: String, default: 'Time' }
});

const emit = defineEmits(['update:modelValue']);

const menuOpen = ref(false);

const dateStr = computed(() => (props.modelValue ? props.modelValue.split('T')[0] : null));
const timeStr = computed(() => {
  if (!props.modelValue) return '00:00';
  const t = props.modelValue.split('T')[1];
  return t ? t.slice(0, 5) : '00:00';
});

const pickerValue = computed(() => {
  if (!dateStr.value) return null;
  const [y, m, d] = dateStr.value.split('-').map(Number);
  return new Date(y, m - 1, d);
});

const displayValue = computed(() => {
  if (!dateStr.value) return '';
  const [y, m, d] = dateStr.value.split('-');
  const datePart = `${d}.${m}.${y}`;
  return props.withTime ? `${datePart} ${timeStr.value}` : datePart;
});

function emitValue(newDateStr, newTimeStr) {
  if (!newDateStr) {
    emit('update:modelValue', null);
    return;
  }
  emit('update:modelValue', props.withTime ? `${newDateStr}T${newTimeStr || '00:00'}` : newDateStr);
}

function onPickDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  emitValue(`${y}-${m}-${d}`, timeStr.value);
  if (!props.withTime) menuOpen.value = false;
}

function onTimeChange(val) {
  emitValue(dateStr.value, val || '00:00');
}
</script>
